import dayjs from 'dayjs';
import * as taxApi from '../../lib/api/taxbill';
import * as yup from 'yup';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  listTaxbills,
  setTaxbillsFilters,
} from '../../modules/taxbillModule/taxbills';
import { Button, Stack, Typography, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useFormik } from 'formik';

import { useBoolean } from '../../hooks/useBoolean';

import TaxbillList from '../../components/taxbillmng/TaxbillList';
import BreadcrumbsContainer from '../common/breadcurmbs/BreadcrumbsContainer';
import { TaxbillListBox } from '../../components/taxbillmng/TaxbillListBox';
import TaxbillStatusList from '../../components/taxbillmng/TaxbillStatusList';
import AskDialog from '../../components/common/dialog/AskDialog';
import TaxbillTotalSummary from '../../components/taxbillmng/TaxbillTotalSummary';
import TaxbillEditModal from '../../components/taxbillmng/TaxbillEditModal';

const TaxbillListContainer = () => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const { totalPrice, taxbills, defaultFilters, error, loading } = useSelector(
    ({ taxbillModule, loading }) => ({
      totalPrice: taxbillModule.taxbills.totalPrice,
      taxbills: taxbillModule.taxbills.taxbills,
      defaultFilters: taxbillModule.taxbills.defaultFilters,
      error: taxbillModule.taxbills.error,
      loading: loading['taxbill/LIST_TAXBILLS'],
    }),
  );

  const [tableData, setTableData] = useState([]);

  const makeFilters = (filters) => {
    // 1. 시작일: 값이 있으면 포맷팅, 없으면 올해 1월 1일
    const startDate =
      filters.searchStrtDt !== null && filters.searchStrtDt !== ''
        ? dayjs(filters.searchStrtDt).format('YYYY-MM-DD')
        : dayjs().startOf('year').format('YYYY-MM-DD'); // 올해 1월 1일

    // 2. 종료일: 값이 있으면 포맷팅, 없으면 오늘 날짜
    const endDate =
      filters.searchEndDt !== null && filters.searchEndDt !== ''
        ? dayjs(filters.searchEndDt).format('YYYY-MM-DD')
        : dayjs().format('YYYY-MM-DD'); // 오늘

    const searchData = {
      osTran: filters.searchOsTran,
      invTyp: filters.searchInvTyp,
      searchTyp: filters.searchTyp,
      reqCompYn: filters.searchYn,
      [filters.searchKeyword]: filters.searchWords,
      strtDt: startDate,
      endDt: endDate,
      prjTyp: filters.prjTyp,
    };

    return searchData;
  };

  useEffect(() => {
    dispatch(listTaxbills(makeFilters(defaultFilters)));
  }, [dispatch, defaultFilters]);

  useEffect(() => {
    if (taxbills) {
      setTableData(taxbills);
    }
  }, [taxbills]);

  const handleFilters = useCallback(
    (name, value) => {
      const newFilters = { ...defaultFilters, [name]: value };

      dispatch(setTaxbillsFilters(newFilters));
    },
    [defaultFilters, dispatch],
  );

  const [selectedItem, setSelectedItem] = useState(null);
  const [removeVisible, setRemoveVisible] = useState(false);
  const handleRemove = useCallback((row) => {
    setRemoveVisible(true);
    setSelectedItem(row);
  }, []);
  const onRemoveCancle = () => {
    setRemoveVisible(false);
    setSelectedItem(null);
  };
  const onRemoveConfirm = () => {
    //매출 취소
    onRemove();
    setRemoveVisible(false);
    setSelectedItem(null);
  };

  const [selectedCancle, setSelectedCancle] = useState(null);
  const showCancle = useBoolean();
  const handleCancle = useCallback((row) => {
    showCancle.onTrue();
    setSelectedCancle(row);
  }, []);

  const onCancleCancle = () => {
    showCancle.onFalse();
    setSelectedCancle(null);
  };
  const onCancleConfirm = () => {
    //발급 취소
    onCancle();
    showCancle.onFalse();
    setSelectedCancle(null);
  };

  const showEdit = useBoolean();
  const [selectedEdit, setSelectedEdit] = useState(null);
  const handleEdit = (param) => {
    setSelectedEdit(param);
    showEdit.onTrue();
  };

  const handleEditClose = () => {
    showEdit.onFalse();
    setSelectedEdit(null);
  };

  const handleEditSave = async (changeDate) => {
    try {
      const editData = {
        invUuid: selectedEdit.invUuid,
        reqCompDt: dayjs(changeDate).format('YYYY-MM-DD'),
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        handleEditClose();
        enqueueSnackbar('발행일이 수정 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onRemove = async () => {
    try {
      const response = await taxApi.removeIssuedTaxbills(
        selectedItem?.prjUuid,
        selectedItem?.invTyp === '매출' ? 'S' : 'B',
        selectedItem?.invUuid,
      );
      if (response.data.result === 'success') {
        enqueueSnackbar('삭제 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const onCancle = async () => {
    try {
      const editData = {
        invUuid: selectedCancle.invUuid,
        reqCompYn: 'N',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        enqueueSnackbar('발급 취소 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const showComplete = useBoolean();
  const [selectedComplete, setSelectedComplete] = useState(null);
  const handleComplete = (param) => {
    setSelectedComplete(param);
    showComplete.onTrue();
  };

  const handleCompleteClose = () => {
    showComplete.onFalse();
    setSelectedComplete(null);
  };

  const handleCompleteSave = async () => {
    try {
      const editData = {
        invUuid: selectedComplete.invUuid,
        reqCompYn: 'Y',
      };
      const response = await taxApi.editTaxbill(editData);
      if (response.data.info) {
        handleCompleteClose();
        enqueueSnackbar('발급확인 되었습니다', {
          variant: 'success',
        });
        dispatch(listTaxbills(makeFilters(defaultFilters)));
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      { field: 'id', hide: true, sortable: false, editable: false },
      { field: 'invUuid', hide: true, sortable: false, editable: false }, //세금계산서ID
      { field: 'roUuid', hide: true, sortable: false, editable: false }, //수주ID
      { field: 'billUuid', hide: true, sortable: false, editable: false }, //거래ID
      { field: 'prjUuid', hide: true, sortable: false, editable: false }, //프로젝트ID
      {
        field: 'invYn',
        hide: true,
        editable: false,
      },
      {
        field: 'invNo',
        headerName: '계산서번호',
        hide: true,
        editable: false,
      },
      {
        field: 'invTyp',
        headerName: '유형',
        hide: true,
        editable: false,
      },

      {
        field: 'reqCompDt',
        headerName: '발행일',
        description: '',
        align: 'center',
        headerAlign: 'center',
        type: 'date',
        flex: 0.8,
        minWidth: 150,
        renderCell: (params) => {
          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="subtitle2">{params.value}</Typography>
              <IconButton
                aria-label="edit"
                color="primary"
                size="small"
                onClick={() => handleEdit(params.row)}
              >
                <EditIcon />
              </IconButton>
            </Stack>
          );
        },
      },
      {
        field: 'partnerNm',
        headerName: '고객명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },

      {
        field: 'cstNm',
        headerName: '사업명',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'center',
      },
      {
        field: 'salesUsrNm',
        headerName: '영업사원',
        description: '영업사원',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'invTxt',
        headerName: '발급내용',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'invAmt',
        headerName: '계산서 금액',
        minWidth: 150,
        flex: 1,
        type: 'number',
      },

      {
        field: 'reqPlanUsrNm',
        headerName: '발행요청자',
        description: '발행요청자',
        type: 'string',
        flex: 0.8,
        minWidth: 120,
      },
      {
        field: 'procCompDt',
        headerName: '지급(수금)완료일자',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 180,
      },
      {
        field: 'procCompYn',
        headerName: '지급(수금) 완료 여부',
        description: '',
        align: 'center',
        headerAlign: 'center',
        flex: 0.8,
        minWidth: 120,
      },

      {
        field: 'action',
        headerName: 'action',
        minWidth: 200,
        flex: 1,
        type: 'string',
        editable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const row = params.row;
          let renderAction;
          if (row.reqCompYn === 'Y') {
            if (row.procCompYn === 'Y') {
              renderAction = (
                <Typography
                  variant="subtitle2"
                  color="success.main"
                  sx={{ mx: 1 }}
                >
                  수금완료
                </Typography>
              );
            } else if (row.procCompYn === 'N') {
              renderAction = (
                <Button
                  size="small"
                  variant="contained"
                  color="inherit"
                  onClick={() => handleCancle(row)}
                >
                  발급취소
                </Button>
              );
            }
          } else if (row.reqCompYn === 'N') {
            renderAction = (
              <Button
                size="small"
                variant="contained"
                color="success"
                onClick={() => handleComplete(row)}
              >
                발급확인
              </Button>
            );
          }

          return (
            <Stack direction="row" alignItems="center" spacing={1}>
              {renderAction}
              <Button
                size="small"
                variant="contained"
                color="error"
                onClick={() => handleRemove(params.row)}
              >
                취소
              </Button>
            </Stack>
          );
        },
      },
    ],
    [],
  );

    //검색 관련 사항
  const initialValues = {
    searchKeyword: defaultFilters.searchKeyword || 'cstNm', // 기본값 설정
    searchWords: defaultFilters.searchWords || '',
    
    // 1. 시작일: 기존값이 없으면 올해 1월 1일 ('YYYY-MM-DD')
    searchStrtDt: defaultFilters.searchStrtDt 
      ? dayjs(defaultFilters.searchStrtDt).format('YYYY-MM-DD') 
      : dayjs().startOf('year').format('YYYY-MM-DD'),

    // 2. 종료일: 기존값이 없으면 오늘 ('YYYY-MM-DD')
    searchEndDt: defaultFilters.searchEndDt 
      ? dayjs(defaultFilters.searchEndDt).format('YYYY-MM-DD') 
      : dayjs().format('YYYY-MM-DD'),

    prjTyp: 'ALL',
  };
  
  const validationSchema = yup.object({
    searchKeyword: yup.string().nullable(),
    searchWords: yup.string().trim().nullable(),
    searchStrtDt: yup.string().nullable(),
    searchEndDt: yup
      .string()
      .nullable()
      .test('is-after-start', '검색 종료일을 확인하세요', function (value) {
        const { searchStrtDt } = this.parent;
        if (searchStrtDt && value) {
          return value >= searchStrtDt; // 간단하게 문자열 비교
        }
        return true;
      })
      .when('searchStrtDt', (searchStrtDt, schema) => {
        if (searchStrtDt) return schema.required('검색 종료일을 입력하세요');
        return schema;
      }),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      const searchData = {
        searchOsTran: defaultFilters.searchOsTran,
        searchInvTyp: defaultFilters.searchInvTyp,
        searchTyp: defaultFilters.searchTyp,
        reqCompYn: defaultFilters.searchYn,
        searchKeyword: values.searchKeyword,
        searchWords: values.searchWords,
        searchStrtDt: values.searchStrtDt,
        searchEndDt: values.searchEndDt,
        prjTyp: values.prjTyp,
      };
      dispatch(setTaxbillsFilters(searchData));
      setTimeout(() => {
        setSubmitting(false);
      }, 1000);
    },
  });
  return (
    <>
      <BreadcrumbsContainer
        title="세금계산서 관리/매출매입 세금계산서/계산서 리스트"
        icon="pay"
      />
      <TaxbillListBox formik={formik} />
      <TaxbillStatusList filters={defaultFilters} onFilters={handleFilters} />
      <TaxbillTotalSummary totalPrice={totalPrice} />
      <TaxbillList
        columns={columns}
        rows={tableData}
        error={error}
        loading={loading}
      />
      <AskDialog
        visible={removeVisible}
        title="매출 취소"
        description={`해당 매출 "${selectedItem?.invTxt}" 을 취소하시겠습니까?`}
        onConfirm={onRemoveConfirm}
        onCancle={onRemoveCancle}
      />
      <AskDialog
        visible={showCancle.value}
        title="발급 취소"
        description={`해당 매출 "${selectedCancle?.invTxt}" 을 발급 취소하시겠습니까?`}
        onConfirm={onCancleConfirm}
        onCancle={onCancleCancle}
      />
      <AskDialog
        visible={showComplete.value}
        title="발급 완료"
        description={`해당 매출 "${selectedComplete?.invTxt}" 을 발급 완료하시겠습니까?`}
        onConfirm={handleCompleteSave}
        onCancle={handleCompleteClose}
      />
      {showEdit && (
        <TaxbillEditModal
          currentData={selectedEdit}
          open={showEdit.value}
          handleClose={handleEditClose}
          onSave={handleEditSave}
        />
      )}
    </>
  );
};

export default TaxbillListContainer;
